import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject, useParams } from 'react-router-dom';
// The mailbox connect's one owner (F20-i FR-17): eager, like the frame it wraps, because it runs on every load.
import { MailboxConnectProvider } from '../features/email/connect/MailboxConnectProvider';
// The tag types and their titles, a few constants, so Settings has one route per type.
import {
    TAG_SETTINGS_KEYS,
    TAG_SETTINGS_TITLES,
    TAG_SETTINGS_TYPES,
} from '../features/settings/tagSettingsTypes';
import { AppLayout } from './AppLayout';
import { AuthenticatedGate } from './AuthenticatedGate';
import { accountPanelComposition } from './accountPanels';
import { contactPanelComposition } from './contactPanels';
import { LandingRoute } from './LandingRoute';
import { leadPanelComposition } from './leadPanels';
import { RouteErrorElement } from './RouteErrorElement';
import { RoutePermissionGuard } from './RoutePermissionGuard';
import { RouteSkeleton } from './RouteSkeleton';
import { RouteTitle } from './RouteTitle';

// lazy() from the first route, not once the bundle "gets big": there is no server render to hide
// a fat initial download behind, and retrofitting splitting is far more work than starting with
// it (coding-standards §3). Each of these becomes its own chunk, which is what FS-1 AC-15 checks.
//
// The interim index → DashboardRoute is gone: Dashboard is F17 (Phase 2), and the index now lands
// on the first permission-accessible section (LandingRoute, F5-b FR-12). Each Phase-1 section is a
// placeholder route until its owning feature ships its screen (F5-b FR-3); the section paths mirror
// navConfig[].path so a NavLink to={item.path} resolves.
const AccountsRoute = lazy(() => import('../features/accounts/AccountsRoute'));
// The account page (F1-d): its own chunk, a flat sibling of `accounts`. New account and Edit account are
// dialogs, over the list and over this page.
const AccountDetailRoute = lazy(() => import('../features/accounts/AccountDetailRoute'));
// The contacts page (F1-f): its own chunk, so a role without `account.view` never downloads it.
const ContactsRoute = lazy(() => import('../features/accounts/ContactsRoute'));
// The contact page (design-system §6 Detail page, Rev 70): its own chunk, a flat sibling of `contacts`.
const ContactRoute = lazy(() => import('../features/accounts/ContactRoute'));
const LeadsRoute = lazy(() => import('../features/leads/LeadsRoute'));
// A lead's page (design-system §6 *Detail page*): its own chunk, for `leads/:id` and `leads/:id/edit`.
const LeadPageRoute = lazy(() => import('../features/leads/LeadPageRoute'));
// F3-a: the Activities page — follow-ups on accounts and leads. Its own lazy chunk, named `<Name>Route`
// so the bundle-budget script sizes it as a route chunk.
const ActivitiesRoute = lazy(() => import('../features/activities/ActivitiesRoute'));
const EmailRoute = lazy(() => import('../features/email/EmailRoute'));
// A flat sibling of `email`, not a nested child: the mailbox screen replaces the inbox rather than
// showing beside it. Its own lazy chunk, like every other route (F20-a FR-12).
const MailboxRoute = lazy(() => import('../features/email/MailboxRoute'));
// One message: the `email` route's child, which `EmailRoute` shows in its reading pane or alone; its own chunk.
const MessageRoute = lazy(() => import('../features/email/MessageRoute'));
// The email templates list and editor (F20-d): Settings pages (`settings/email-templates…`), each its own chunk.
const EmailTemplatesRoute = lazy(() => import('../features/email/templates/EmailTemplatesRoute'));
const EmailTemplateEditorRoute = lazy(() => import('../features/email/templates/EmailTemplateEditorRoute'));
const PricingRoute = lazy(() => import('../features/pricing/PricingRoute'));
// A flat sibling of `pricing`, as `users/:id` is of `users`: the book renders no <Outlet/>. Its own
// lazy chunk, named `<Name>Route` so the bundle-budget script sizes it as a route chunk (F26-c).
const PricingImportRoute = lazy(() => import('../features/pricing/PricingImportRoute'));
const UsersRoute = lazy(() => import('../features/users/UsersRoute'));
// A flat sibling of `users`, as `roles/:id` is of `roles`: the list renders no <Outlet/>. Its own
// lazy chunk, named `<Name>Route` so the bundle-budget script sizes it as a route chunk.
const UserEditorRoute = lazy(() => import('../features/users/UserEditorRoute'));
// F5-h: the offboarding screen, a sub-view of the user editor. Its own lazy chunk, named `<Name>Route`.
const UserOffboardRoute = lazy(() => import('../features/users/UserOffboardRoute'));
const RolesRoute = lazy(() => import('../features/roles/RolesRoute'));
// A flat sibling of `roles`, like `email/mailbox`: the list renders no <Outlet/>. Its own lazy chunk;
// the `<Name>Route` file name keeps the bundle-budget script sizing it as a route chunk.
const RoleEditorRoute = lazy(() => import('../features/roles/RoleEditorRoute'));
// F1-e: Settings › Tags, the Settings entry's first page. Its own lazy chunk, named `<Name>Route`.
const SettingsTagsRoute = lazy(() => import('../features/settings/SettingsTagsRoute'));
// The Settings page of links (design-system §8, Rev 70).
const SettingsRoute = lazy(() => import('../features/settings/SettingsRoute'));
const NotFoundRoute = lazy(() => import('./NotFoundRoute'));
const LoginRoute = lazy(() => import('../features/auth/LoginRoute'));

/** `/accounts/:id/edit` → `/accounts/:id`, replacing the history entry. */
function AccountEditRedirect() {
    return <Navigate replace to={`/accounts/${useParams().id ?? ''}`} />;
}

/**
 * The route tree, exported so `router.test.tsx` renders the real one — its guards included — rather
 * than a hand-written mirror.
 */
export const appRoutes: RouteObject[] = [
    {
        // Pathless layout route: AuthenticatedGate wraps the whole authenticated tree so no route
        // below it renders before GET /api/v1/me resolves (no flash), redirecting to /login on 401.
        // The `/login` route is a sibling OUTSIDE this gate. The error element catches a throw in the
        // gate itself; everything below it has the `/` route's own. It offers a reload, because the
        // dashboard it would otherwise send the user to sits behind this same gate (BUG-053).
        element: <AuthenticatedGate />,
        errorElement: <RouteErrorElement recovery="reload" />,
        children: [
            {
                path: '/',
                // The mailbox connect wraps the frame (F20-i WI-1.3 plan D-4): inside the gate's permission
                // provider, so it reads `email.manage`, and around every page, so its failure shows on any.
                element: (
                    <MailboxConnectProvider>
                        <AppLayout />
                    </MailboxConnectProvider>
                ),
                // Every route inherits this, so no future route can ship without an honest failure path.
                errorElement: <RouteErrorElement />,
                children: [
                    // Eager, like AppLayout: LandingRoute is app chrome (a permission-driven redirect),
                    // not a lazy feature chunk. It replaces the interim index → DashboardRoute (F5-b FR-12).
                    { index: true, handle: { title: 'Home', theme: 'revamp' }, element: <LandingRoute /> },
                    // Every section route is guarded on its navigation entry's key (F5-k FR-17), so a
                    // deep link is gated exactly like the sidebar and a user without the key sees the
                    // no-access state in place. The guard sits outside the lazy element, so the
                    // section's chunk is never downloaded without the key. `email/mailbox` has no
                    // entry of its own and takes the Email key. The index and `*` stay unguarded.
                    {
                        path: 'accounts',
                        handle: { title: 'Accounts', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="accounts">
                                <AccountsRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The account page (F1-d FR-V1): its section's guard (`account.view`), so a role
                        // without it gets the no-access state and never loads the page. The composition
                        // of other features' panels is handed over here, the composition root
                        // (F1-d FR-S2).
                        path: 'accounts/:id',
                        handle: { title: 'Accounts', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="accounts">
                                <AccountDetailRoute composition={accountPanelComposition} />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // Edit account is a dialog over the list and over the account page, not a page:
                        // an old link or bookmark to the edit page opens the account page. Titled and
                        // guarded on the section key as every other account route is, so a role without
                        // it gets the no-access state here rather than a redirect.
                        path: 'accounts/:id/edit',
                        handle: { title: 'Accounts', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="accounts">
                                <AccountEditRedirect />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The contacts page (F1-f FR-N1), on shadcn/ui in the revamped colour layer
                        // (AD #39, RouteTheme).
                        path: 'contacts',
                        handle: { title: 'Contacts', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="contacts">
                                <ContactsRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The contact page (design-system §6 Detail page, Rev 70): its section's guard, so a
                        // role without `account.view` gets the no-access state and never loads the page. The
                        // contact's places are handed over here, the composition root (F1-f FR-V2). A record
                        // page carries its section's name; the loaded contact's name goes first
                        // (`useRecordTitle` in `ContactPage`, design-system §9).
                        path: 'contacts/:id',
                        handle: { title: 'Contacts', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="contacts">
                                <ContactRoute composition={contactPanelComposition} mode="view" />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The contact page with the edit form open. The same element as `contacts/:id`, so
                        // the page stays mounted between the two; the form itself needs `account.manage`,
                        // which the page checks (an edit address without it becomes the view address).
                        path: 'contacts/:id/edit',
                        handle: { title: 'Contacts', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="contacts">
                                <ContactRoute composition={contactPanelComposition} mode="edit" />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        path: 'leads',
                        // The list is on shadcn/ui (shadcn-backfill.md § How a screen is moved over, step 1);
                        // the lead page opts in with its own PR.
                        handle: { title: 'Leads', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="leads">
                                <LeadsRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // A lead's page (design-system §6 *Detail page*): a flat sibling of `leads`, which
                        // reads the id from the path, under its section's guard, as `users/:id` is. A record
                        // page carries its section's name; the loaded lead's name goes first
                        // (`useRecordTitle` in `LeadView`, design-system §9).
                        path: 'leads/:id',
                        handle: { title: 'Leads', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="leads">
                                <LeadPageRoute composition={leadPanelComposition} />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The same page with its edit form open — the list's row menu *Edit* and the page's
                        // own *Edit* (design-system §6 *List screen*, Rev 70).
                        path: 'leads/:id/edit',
                        handle: { title: 'Leads', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="leads">
                                <LeadPageRoute composition={leadPanelComposition} />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The Activities page (F3-a FR-V1), on shadcn/ui in the revamped colour layer
                        // (AD #39, RouteTheme). Its entry gates on `account.view` or `lead.view` (FR-V7).
                        path: 'activities',
                        handle: { title: 'Activities', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="activities">
                                <ActivitiesRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The inbox, on the revamped colour layer (AD #39), as its child, the message view, is.
                        path: 'email',
                        handle: { title: 'Email', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="email">
                                <EmailRoute />
                            </RoutePermissionGuard>
                        ),
                        children: [
                            {
                                // One message: the inbox's child, shown in its reading pane at `bp/laptop`
                                // and wider and alone below it (`EmailRoute`). The section's guard covers
                                // it. The loaded subject comes first in the page title (`useRecordTitle`).
                                path: 'messages/:id',
                                handle: { title: 'Email', theme: 'revamp' },
                                element: <MessageRoute />,
                            },
                        ],
                    },
                    {
                        path: 'email/mailbox',
                        handle: { title: 'Mailbox', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="email">
                                <MailboxRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        path: 'pricing',
                        // Revamp (AD #39): the Pricing page renders in the revamped colour layer (RouteTheme).
                        handle: { title: 'Pricing', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="pricing">
                                <PricingRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The weekly price import (F26-c FR-15): inside the Pricing section's guard,
                        // and needing `buying_standard.manage` when it is entered, so a user without
                        // the key gets the no-access state and never downloads the page's chunk.
                        path: 'pricing/import',
                        handle: { title: 'Import weekly prices', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard entryPermission="buying_standard.manage" nav="pricing">
                                <PricingImportRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // Settings (design-system §8, Rev 70): a page of links, one card per page below, open to a
                        // holder of any Settings page's key (the Settings entry's keys). Each page below is guarded
                        // on the key its card names (`settingsPages.ts`, AD #41).
                        path: 'settings',
                        handle: { title: 'Settings', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard nav="settings">
                                <SettingsRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    // One entity type's tags (F1-e FR-T1), each its own page and card, on its own key.
                    ...TAG_SETTINGS_TYPES.map(
                        (type): RouteObject => ({
                            path: `settings/tags/${type}`,
                            handle: { title: TAG_SETTINGS_TITLES[type], theme: 'revamp' },
                            element: (
                                <RoutePermissionGuard permission={TAG_SETTINGS_KEYS[type]}>
                                    <SettingsTagsRoute key={type} type={type} />
                                </RoutePermissionGuard>
                            ),
                        }),
                    ),
                    {
                        // The email templates (F20-d FR-S1), moved from Email into Settings (Rev 70), on
                        // `settings.email_templates.manage` (AD #41), and the revamped colour layer (AD #39). Without
                        // `email.manage` a user writes shared templates only (`presetKindFor`).
                        path: 'settings/email-templates',
                        handle: { title: 'Email templates', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.email_templates.manage">
                                <EmailTemplatesRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // One template in view mode (F20-d FR-S3; design-system §6 *Detail page*, Rev 70):
                        // the loaded name goes first in the page title (`useRecordTitle` in the view). A new
                        // template is written in the dialog over the list, so there is no `new` address.
                        path: 'settings/email-templates/:id',
                        handle: { title: 'Email templates', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.email_templates.manage">
                                <EmailTemplateEditorRoute mode="view" />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The same template in edit mode: the ⋯ menu's and the page's *Edit*.
                        path: 'settings/email-templates/:id/edit',
                        handle: { title: 'Email templates', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.email_templates.manage">
                                <EmailTemplateEditorRoute mode="edit" />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // Users, moved into Settings (Rev 70), on `settings.users.manage`. Revamp (AD #39):
                        // the Users routes render in the revamped colour layer (RouteTheme).
                        path: 'settings/users',
                        handle: { title: 'Users', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.users.manage">
                                <UsersRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The user's page in view mode (design-system §6 Detail page, Rev 70). A record page
                        // carries its section's name; the loaded user's name goes first (`useRecordTitle` in
                        // `UserView` and `UserEditorForm`, design-system §9).
                        path: 'settings/users/:id',
                        handle: { title: 'Users', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.users.manage">
                                <UserEditorRoute mode="view" />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The same page in edit mode: the list's ⋯ Edit and the view's Edit open it.
                        path: 'settings/users/:id/edit',
                        handle: { title: 'Users', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.users.manage">
                                <UserEditorRoute mode="edit" />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // F5-h: the offboarding screen (spec FR-U1), a sub-view of the user's page. The loaded
                        // user's name goes first in the page title (`useRecordTitle` in `OffboardScreen`).
                        path: 'settings/users/:id/offboard',
                        handle: { title: 'Offboard user', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.users.manage">
                                <UserOffboardRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // Roles, moved into Settings (Rev 70), on `settings.roles.manage`.
                        path: 'settings/roles',
                        handle: { title: 'Roles', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.roles.manage">
                                <RolesRoute />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The role's page in view mode (design-system §6 Detail page, Rev 70). A record page
                        // carries its section's name; the loaded role's name goes first (`useRecordTitle` in
                        // `RoleView` and `RoleEditorForm`, design-system §9).
                        path: 'settings/roles/:id',
                        handle: { title: 'Roles', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.roles.manage">
                                <RoleEditorRoute mode="view" />
                            </RoutePermissionGuard>
                        ),
                    },
                    {
                        // The same page in edit mode: the list's ⋯ Edit, the view's Edit and New role open it.
                        path: 'settings/roles/:id/edit',
                        handle: { title: 'Roles', theme: 'revamp' },
                        element: (
                            <RoutePermissionGuard permission="settings.roles.manage">
                                <RoleEditorRoute mode="edit" />
                            </RoutePermissionGuard>
                        ),
                    },
                    // Explicit catch-all: without it an unknown path renders the layout with an empty
                    // outlet, which looks like a broken page rather than a wrong address. It is its own
                    // component, not the error element reused — an error element rendered as an element
                    // has no route error to read, so it would fall through to its generic "something
                    // failed" branch and report a mistyped URL as a fault.
                    {
                        path: '*',
                        handle: { title: 'Page not found', theme: 'revamp' },
                        element: <NotFoundRoute />,
                    },
                ],
            },
        ],
    },
    {
        // The login page is a top-level sibling OUTSIDE the AuthenticatedGate, so an unauthenticated
        // user can reach it without the gate redirecting in a loop. AppLayout's single Suspense
        // boundary only covers the gated tree, so this ungated lazy element carries its own — a lazy
        // element with no Suspense ancestor throws at render.
        path: '/login',
        handle: { title: 'Sign in' },
        element: (
            <>
                <RouteTitle />
                <Suspense fallback={<RouteSkeleton />}>
                    <LoginRoute />
                </Suspense>
            </>
        ),
        // Without this a failed `LoginRoute` chunk (a tab left open across a deploy) showed React
        // Router's built-in "Unexpected Application Error!". Reload, not "Back to the dashboard": an
        // unauthenticated user sent to the dashboard is sent straight back here (BUG-053).
        errorElement: <RouteErrorElement recovery="reload" what="the sign-in page" />,
    },
];

export const router = createBrowserRouter(appRoutes);
